import { GoodReportDetail, GoodReport, ProductUnit } from '@core/services/data.service';
import { Multilingual } from '@core/utilities/multilingual';

export class Detail implements GoodReportDetail {

  private source: GoodReportDetail[] = [];

  public get Unit(): ProductUnit {
    return this.source.first(i => i.Unit == ProductUnit.Meter) ? (this.source.first(i => i.Unit == ProductUnit.Kilogram) ? ProductUnit.Unknown : ProductUnit.Meter) : this.source.first(i => i.Unit == ProductUnit.Kilogram) ? ProductUnit.Kilogram : ProductUnit.Unknown; 
  }

  public get LengthCount(): number {
    return this.source.sum(i => i.LengthCount);
  }
  public get LengthQuantity(): number {
    return this.source.sum(i => i.LengthQuantity);
  }

  public get LengthQuoteQuantity(): number {
    return this.source.sum(i => i.LengthQuoteQuantity);
  }

  public get LengthDiscount(): number {
    return this.source.sum(i => i.LengthDiscount);
  }

  public get WeightCount(): number {
    return this.source.sum(i => i.WeightCount);
  }
  public get WeightQuantity(): number {
    return this.source.sum(i => i.WeightQuantity);
  }

  public get WeightQuoteQuantity(): number {
    return this.source.sum(i => i.WeightQuoteQuantity);
  }

  public get WeightDiscount(): number {
    return this.source.sum(i => i.WeightDiscount);
  }

  public get BaseWorth(): number {
    return this.source.sum(i => i.BaseWorth);
  }
  public get QuoteWorth(): number {
    return this.source.sum(i => i.QuoteWorth);
  }
  public get QuoteValue(): number {
    return this.source.sum(i => i.QuoteValue);
  }

  public get DiscountBaseWorth(): number {
    return this.source.sum(i => i.DiscountBaseWorth);
  }

  public get DiscountQuoteWorth(): number {
    return this.source.sum(i => i.DiscountQuoteWorth);
  }

  public get DiscountQuoteValue(): number {
    return this.source.sum(i => i.DiscountQuoteValue);
  }

  constructor() {
  }

  public add(detail: GoodReportDetail): void {
    
    if (detail) {
      this.source.push(detail);
    }
  }

  public exchange(type: 'product' | 'discount'): number {
    switch (type) {
      case 'product': return this.QuoteWorth != 0 ? this.QuoteValue / this.QuoteWorth : 0;
      case 'discount': return this.DiscountQuoteWorth != 0 ? this.DiscountQuoteValue / this.DiscountQuoteWorth : 0;
      default: return 0;
    }
  }

  public base(type: 'product' | 'discount', currency: 'CNY' | 'VND'): number {

    switch (currency) {

      case 'CNY':
        switch (type) {
          case 'product': return this.BaseWorth;
          case 'discount': return this.DiscountBaseWorth;
          default: return 0;
        }

      case 'VND':
        return this.base(type, 'CNY') * this.exchange(type);

      default: return 0;
    }
  }

  public quote(type: 'product' | 'discount', currency: 'CNY' | 'VND'): number {
    switch (currency) {

      case 'CNY':

        switch (type) {
          case 'product': return this.QuoteWorth;
          case 'discount': return this.DiscountQuoteWorth;
          default: return 0;
        }

      case 'VND':

        switch (type) {
          case 'product': return this.QuoteValue;
          case 'discount': return this.DiscountQuoteValue;
          default: return 0;
        }

      default: return 0;
    }
  }

  public gross(type: 'product' | 'discount', currency: 'CNY' | 'VND'): number {
    return this.quote(type, currency) - this.base(type, currency);
  }

  public total(type: 'gross' | 'base', currency: 'CNY' | 'VND'): number {
    switch (type) {
      case 'gross': return this.gross('product', currency) - this.gross('discount', currency);
      case 'base': return this.base('product', currency) - this.base('discount', currency);
    }
  }
}

export class Report implements GoodReport {

  public static reset(name: string) {
    this.lookup = {};
    this.root = new Report(name);
  }

  private static lookup: { [key: string]: Report } = {};

  public static add(report: GoodReport) {

    let current = this.root;
    let path = this.root.name;

    for (let name of report.Path) {
      path += '/' + name;

      if (!this.lookup[path]) {

        this.lookup[path] = new Report(name);
        this.lookup[path].Path = path.split('/').skip(1);
        current.add(this.lookup[path]);
      }

      current = this.lookup[path];
    }

    current.add(report);
  }

  public Path: string[];

  public name: string;
  public parent: Report = null;
  public children: Report[] = [];

  public static root: Report = new Report('');

  public Initial: Detail;
  public Imports: Detail;
  public Exports: Detail;
  public CompanyLosses: Detail;
  public ProviderLosses: Detail;
  public Returns: Detail;

  constructor(name: string) {
    this.name = name;
    this.Initial = new Detail();
    this.Imports = new Detail();
    this.Exports = new Detail();
    this.CompanyLosses = new Detail();
    this.ProviderLosses = new Detail();
    this.Returns = new Detail();
  }

  public add(report: Report | GoodReport) {    

    if (report['name']) {
      this.children.push(report as Report);
      (report as Report).parent = this;
    }

    this.Initial.add(report.Initial);
    this.Imports.add(report.Imports);
    this.Exports.add(report.Exports);
    this.CompanyLosses.add(report.CompanyLosses);
    this.ProviderLosses.add(report.ProviderLosses);
    this.Returns.add(report.Returns);
  }

  public gross(type: 'export' | 'return' | 'discount' | 'total', currency: 'CNY' | 'VND') {
    switch (type) {
      case 'export': return this.Exports.gross('product', currency);
      case 'return': return -this.Returns.gross('product', currency);
      case 'discount': return this.base('discount', currency) - this.quote('discount', currency);
      case 'total': return Math.round(this.gross('export', currency) + this.gross('return', currency) + this.gross('discount', currency) - this.base('lost', currency));
      default: return 0;
    }
  }

  public base(type: 'initial' | 'import' | 'provider' | 'export' | 'profitable' | 'return' | 'discount' | 'lost' | 'left', currency: 'CNY' | 'VND') {
    switch (type) {
      case 'initial': return this.convert(this.Initial.base('product', 'CNY'), currency);
      case 'provider': return this.convert(this.ProviderLosses.base('product', 'CNY'), currency);
      case 'import': return this.convert(this.Imports.base('product', 'CNY'), currency);        
      case 'export': return this.Exports.base('product', currency);
      case 'return': return this.Returns.base('product', currency);
      case 'discount': return this.Imports.base('discount', currency);
      case 'lost': return this.CompanyLosses.base('product', currency);
      case 'left': return this.left('quantity') == 0 ? 0 : this.base('initial', currency) + this.base('import', currency) - this.base('provider', currency) - this.base('export', currency) + this.base('return', currency) - this.base('lost', currency);
      case 'profitable': return this.base('export', currency) - this.base('discount', currency) - this.base('lost', currency);       
      default: return 0;
    }
  }

  public quote(type: 'initial' | 'import' | 'provider' | 'export' | 'profitable' | 'return' | 'discount' | 'lost', currency: 'CNY' | 'VND') {
    switch (type) {
      case 'initial': return this.convert(this.Initial.quote('product', 'CNY'), currency);
      case 'provider': return this.convert(this.ProviderLosses.quote('product', 'CNY'), currency);
      case 'import': return this.convert(this.Imports.quote('product', 'CNY'), currency);        
      case 'export': return this.Exports.quote('product', currency);
      case 'return': return this.Returns.quote('product', currency);
      case 'discount': return this.Exports.quote('discount', currency);
      case 'lost': return this.CompanyLosses.quote('product', currency);
      case 'profitable': return this.quote('export', currency) - this.quote('discount', currency) - this.quote('lost', currency);
      default: return 0;
    }
  }

  public left(type: 'count' | 'quantity' | 'length-count' | 'length-quantity' | 'weight-count' | 'weight-quantity'): number {
    switch (type) {
      case 'length-count': return this.Initial.LengthCount + this.Imports.LengthCount + this.Returns.LengthCount - this.ProviderLosses.LengthCount - this.Exports.LengthCount - this.CompanyLosses.LengthCount;
      case 'weight-count': return this.Initial.WeightCount + this.Imports.WeightCount + this.Returns.WeightCount - this.ProviderLosses.WeightCount - this.Exports.WeightCount - this.CompanyLosses.WeightCount;
      case 'length-quantity': return this.Initial.LengthQuantity + this.Imports.LengthQuantity + this.Returns.LengthQuantity - this.ProviderLosses.LengthQuantity - this.Exports.LengthQuantity - this.CompanyLosses.LengthQuantity;
      case 'weight-quantity': return this.Initial.WeightQuantity + this.Imports.WeightQuantity + this.Returns.WeightQuantity - this.ProviderLosses.WeightQuantity - this.Exports.WeightQuantity - this.CompanyLosses.WeightQuantity;
      case 'count': return this.Initial.LengthCount + this.Initial.WeightCount + this.Imports.LengthCount + this.Imports.WeightCount + this.Returns.LengthCount + this.Returns.WeightCount - this.ProviderLosses.LengthCount - this.ProviderLosses.WeightCount - this.Exports.LengthCount - this.Exports.WeightCount - this.CompanyLosses.LengthCount - this.CompanyLosses.WeightCount;
      case 'quantity': return this.Initial.LengthQuantity + this.Initial.WeightQuantity + this.Imports.LengthQuantity + this.Imports.WeightQuantity + this.Returns.LengthQuantity + this.Returns.WeightQuantity - this.ProviderLosses.LengthQuantity - this.ProviderLosses.WeightQuantity - this.Exports.LengthQuantity - this.Exports.WeightQuantity - this.CompanyLosses.LengthQuantity - this.CompanyLosses.WeightQuantity;

      default: return 0;
    }
  }

  private convert(value: number, currency: 'VND' | 'CNY'): number {
    switch (currency) {
      case 'CNY': return value;
      case 'VND': return value * this.exchange;
      default: return 0;
    }
  }

  public get exchange(): number {

    let cny = this.gross('total', 'CNY');
    let vnd = this.gross('total', 'VND');

    return cny != 0 ? vnd / cny : 0;
  }
}
